import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import {
  Product,
  SearchState,
  typeaheadSearchSliceReq,
  debounce,
  updateOriginallySearchedText,
  popularProductsSliceReq,
  overlayContentSearchSliceReq,
  overlayCmsDataSliceReq,
  SearchEvents,
  SearchEventsEnum,
  OnsiteSearchEventData,
  SearchTrackingTypes,
  updateSearchTrackingType,
  searchedProductsSliceReq,
  resetSearchedProducts,
  transformContentCardSearchResults,
  ContentCategory,
} from '@commerce-search-webcomponents/sdk';
import type { CheckoutProfile } from '@commerce-checkout-webcomponents/sdk';
import { Breakpoints, useBreakpoint, useSession, useTranslation } from '@nextgen-web-framework/all';
import { PreferenceService, StorageService } from '@nextgen-web-framework/core';
import { ContentCardCarousel, ModeEnum, VariantEnum } from '@commerce-search-webcomponents/ui';
import { aprMock } from '../../__mocks__/searchOverlayMocks';
import { SearchOverlayProps } from './types/propsTypes';
import Suggestions from './Suggestions/Suggestions';
import Products, { SEARCHED_PRODUCTS_STORAGE_KEY } from './Products/Products';
import { Suggestion } from './Suggestions/types/PropTypes';
import Overlay from './Overlay/Overlay';
import BrandsAndSolutions from './BrandsAndSolutions/BrandsAndSolutions';
import styles from './SearchOverlay.module.scss';
import {
  animationRuntime,
  contentResultsLimit,
  debounceDelay,
  searchTextMinLength,
  contentSearchSite,
} from './types/constants';

const SearchOverlay = ({
  onProductClick,
  onSuggestionClick,
  searchText,
  closeHandler,
  showOverlay,
}: SearchOverlayProps) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState<boolean>(false);
  const [fadeOut, setFadeOut] = useState<boolean>(false);
  const [autoCompleteResults, setAutoCompleteResults] = useState<Array<Suggestion>>([]);
  const [pageSearchResults, setPageSearchResults] = useState<Array<Product>>([]);
  const [isPopularProduct, setIsPopularProduct] = useState<boolean>(false);
  const [isRecentProduct, setIsRecentProduct] = useState(false);
  const [isShowNoResult, setIsShowNoResult] = useState(false);
  const alternateTermRef = useRef<string>('');
  const {
    errors,
    cmsData,
    cmsAPISettled,
    typeaheadSearchResults,
    popularProducts,
    searchedProducts,
    contentSearchLoaders,
    overlayArticlesResults,
    overlayOffersResults,
  } = useSelector((state: { commerceSearch: SearchState }) => state.commerceSearch);
  const { isOverlayOffersLoading = false, isOverlayArticlesLoading = false } = contentSearchLoaders || {};
  const { search_results: searchResultsArticles = [] } = overlayArticlesResults || {};
  const { search_results: searchResultsOffers = [] } = overlayOffersResults || {};
  const { products = [] } = cmsData || {};
  const globalProductFallbackIds = products.map(({ product_id }) => product_id);
  const dispatch = useDispatch();
  const viewPortType = useBreakpoint() as Breakpoints;
  const { session } = useSession();
  const t = useTranslation();
  const { locale } = PreferenceService.getPreferences();
  const storage = StorageService.getLocalStorage();
  const {
    NEXT_PUBLIC_ENABLE_APR: isAprEnabled,
    NEXT_PUBLIC_ENABLE_CONTENT_SEARCH: isContentSearchEnabled,
    NEXT_PUBLIC_SHOW_NO_RESULT: isShowNoResultConfig = false,
  } = ConfigService.getConfigs() as {
    NEXT_PUBLIC_ENABLE_APR?: boolean;
    NEXT_PUBLIC_ENABLE_CONTENT_SEARCH?: boolean;
    NEXT_PUBLIC_SHOW_NO_RESULT?: boolean;
  };
  useEffect(() => {
    if (showOverlay) {
      fetchCMSData();
      checkForRecentProducts();
      if (!searchText && isContentSearchEnabled) {
        fetchContentSearchResults();
      }
    }
    if (!showOverlay && ![Breakpoints.XS, Breakpoints.SM].includes(viewPortType)) {
      setFadeOut(true);
      setTimeout(() => {
        setIsOverlayVisible(false);
        setFadeOut(false);
      }, animationRuntime);
    } else {
      setIsOverlayVisible(showOverlay);
    }
  }, [showOverlay, locale]);

  useEffect(() => {
    if (showOverlay) {
      if (cmsAPISettled) {
        fetchPopularProducts();
      }
    }
  }, [showOverlay, cmsAPISettled]);

  useEffect(() => {
    alternateTermRef.current = '';
    const alphaNumericText = searchText.trim().replace(/[^\p{L}\d]+/gu, '');
    if (alphaNumericText.length >= searchTextMinLength) {
      debounce(debounceDelay, () => typeaheadSearch(searchText));
    } else {
      resetResults();
      checkForRecentProducts();
    }
  }, [searchText, locale]);

  useEffect(() => {
    const { products, suggestions, total, alternateTerms } = typeaheadSearchResults;
    const searchTextHasMinLength = searchText.length >= searchTextMinLength;
    const alternateText = alternateTerms?.[0];
    if (alternateText) {
      alternateTermRef.current = alternateText;
      typeaheadSearch(alternateText);
      trackEvent(SearchTrackingTypes.CORRECTED);
    }
    if (total && total > 0 && searchTextHasMinLength) {
      if (products?.length) {
        setPageSearchResults(products);
        setIsPopularProduct(false);
        setIsRecentProduct(false);
      }
    } else {
      setPageSearchResults([]);
      checkForRecentProducts();
    }
    setIsShowNoResult(total === 0);
    if (suggestions?.length) {
      if (!alternateTermRef.current && searchTextHasMinLength) {
        setAutoCompleteResults(suggestions.slice(0, 3).map((suggestion: string) => ({ title: suggestion })) || []);
        trackEvent(SearchTrackingTypes.AUTOCOMPLETE);
      }
    } else if (searchTextHasMinLength) {
      setAutoCompleteResults([{ title: searchText }]);
    }
  }, [typeaheadSearchResults]);

  const trackEvent = (search_type: SearchTrackingTypes) => {
    if (!errors?.typeaheadSearchError) {
      SearchEvents.publishEvent(SearchEventsEnum.OnsiteSearch, {
        search_type,
        text: searchText,
        search_category: [],
        searchResults: typeaheadSearchResults,
      } as OnsiteSearchEventData);
    }
  };

  const typeaheadSearch = (text: string) => {
    dispatch(typeaheadSearchSliceReq({ text, warehouseId: session.warehouseId }) as unknown as AnyAction);
  };

  const fetchCMSData = () => {
    dispatch(overlayCmsDataSliceReq() as unknown as AnyAction);
  };

  const checkForRecentProducts = () => {
    const cachedProductIds = (storage?.retrieve(SEARCHED_PRODUCTS_STORAGE_KEY) || []) as string[];
    const profileData = storage?.retrieve('checkoutProfile') as unknown as CheckoutProfile;
    if (showOverlay && (cachedProductIds.length != searchedProducts.length || searchedProducts[0]?.locale != locale)) {
      dispatch(
        searchedProductsSliceReq({
          idList: cachedProductIds,
          fulfillmentType: profileData?.fulfillmentType?.id,
          warehouseId: session.warehouseId,
        }) as unknown as AnyAction,
      );
    }
    if (cachedProductIds.length !== 0) {
      setIsRecentProduct(true);
    } else {
      setIsPopularProduct(true);
    }
  };

  const fetchPopularProducts = () => {
    const profileData = storage?.retrieve('checkoutProfile') as unknown as CheckoutProfile;
    const fallbackIds = aprMock.recommendations.map((productId) => productId.sku);
    dispatch(
      popularProductsSliceReq({
        isAprEnabled,
        fallbackIds,
        globalProductFallbackIds,
        fulfillmentType: profileData?.fulfillmentType?.id,
        warehouseId: session.warehouseId,
      }) as unknown as AnyAction,
    );
  };

  const dispatchContentSearchResults = (category: ContentCategory) => {
    dispatch(
      overlayContentSearchSliceReq({
        pageSize: contentResultsLimit,
        market: contentSearchSite,
        category: category,
        locale,
      }) as unknown as AnyAction,
    );
  };

  const fetchContentSearchResults = () => {
    dispatchContentSearchResults(ContentCategory.Article);
    dispatchContentSearchResults(ContentCategory.Offers);
  };

  const resetResults = () => {
    setAutoCompleteResults([]);
    setPageSearchResults([]);
  };

  const handleSuggestionClick: typeof onSuggestionClick = (e, text) => {
    dispatch(updateOriginallySearchedText(alternateTermRef.current && text));
    onSuggestionClick(e, alternateTermRef.current || text);
  };

  const handleProductsClearAllClick = () => {
    dispatch(resetSearchedProducts());
    setIsRecentProduct(false);
    setIsPopularProduct(true);
    storage?.remove(SEARCHED_PRODUCTS_STORAGE_KEY);
  };

  const handleViewAllClick = (e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
    dispatch(updateOriginallySearchedText(''));
    dispatch(
      updateSearchTrackingType(alternateTermRef.current ? SearchTrackingTypes.CORRECTED : SearchTrackingTypes.ENTERED),
    );
    onSuggestionClick(e, alternateTermRef.current || searchText);
  };

  let productList = pageSearchResults;
  if (pageSearchResults.length === 0) {
    productList = (isRecentProduct ? searchedProducts : popularProducts) as Product[];
  }

  return (
    <>
      {isOverlayVisible && (
        <div className={styles['search-overlay-container']}>
          <Overlay fadeOut={fadeOut} onBackdropClick={closeHandler}>
            <div className={styles['overlay-content']}>
              <Suggestions
                autoCompleteResults={autoCompleteResults}
                searchText={searchText}
                isShowNoResult={isShowNoResult && isShowNoResultConfig}
                onSuggestionClick={handleSuggestionClick}
              />
              <div className={styles['right-column']}>
                {productList.length > 0 && (
                  <Products
                    searchText={searchText}
                    searchTextMinLength={searchTextMinLength}
                    isPopularProduct={isPopularProduct}
                    isRecentProduct={isRecentProduct}
                    alternateTerm={alternateTermRef.current}
                    typeaheadSearchResults={typeaheadSearchResults}
                    productList={productList}
                    onClearAllClick={handleProductsClearAllClick}
                    onProductClick={onProductClick}
                    onViewAllClick={handleViewAllClick}
                  />
                )}
                {!searchText && isContentSearchEnabled && searchResultsOffers && (
                  <ContentCardCarousel
                    loading={isOverlayOffersLoading}
                    loadingItems={3}
                    mode={ModeEnum.OFFER}
                    variant={VariantEnum.SEARCH_OVERLAY}
                    title={t('search.searchOverlay.featuredOffers')}
                    cards={transformContentCardSearchResults(searchResultsOffers?.slice(0, contentResultsLimit))}
                  />
                )}
                <BrandsAndSolutions
                  buckets={typeaheadSearchResults.buckets}
                  showPopular={searchText.length < searchTextMinLength}
                />
                {!searchText && isContentSearchEnabled && searchResultsArticles && (
                  <ContentCardCarousel
                    loading={isOverlayArticlesLoading}
                    loadingItems={3}
                    mode={ModeEnum.ARTICLE}
                    variant={VariantEnum.SEARCH_OVERLAY}
                    title={t('search.searchOverlay.recommendedArticles')}
                    cards={transformContentCardSearchResults(searchResultsArticles?.slice(0, contentResultsLimit))}
                  />
                )}
                <div className={styles['gutter-space']}></div>
              </div>
            </div>
          </Overlay>
        </div>
      )}
    </>
  );
};

export default SearchOverlay;
export type { SearchOverlayProps };
